import { useState, useCallback } from "react";
import { useInventoryFreshness } from "database/useDatabaseReader";
import { colorCodeRanking } from "helpers/stormtechColorCodes";
import { getProductsByItemNumber, groupByItemNumber } from "services/supabaseSearch";
import { processInventoryData } from "helpers/typesenseInventoryTransform";
import { useDatabaseReader } from "database/useDatabaseReader";

const useSelectedArticle = () => {
  const { warehouses } = useDatabaseReader();

  const [selectedArticleData, setSelectedArticleData] = useState([]);
  const [currentInventoryLoading, setCurrentInventoryLoading] = useState(false);
  const [currentInventory, setCurrentInventory] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lastSearchStyle, setLastSearchStyle] = useState(null);

  const onSelectStyle = async (style) => {
    setCurrentInventory([]);
    setCurrentInventoryLoading(true);
    setLastSearchStyle(style); // Store the search style for auto-reload

    const searchTerm = ["W", "Y", "S", "M", "L"].includes(
      style.value[style.value.length - 1],
    )
      ? style.value.slice(0, -1)
      : style.value;

    const products = await getProductsByItemNumber(searchTerm);
    const allGroupedHits = groupByItemNumber(products);

    const finalResult = [];

    allGroupedHits.forEach((group) => {
      const groupHits = group.colors;

      //Sort the colors
      const singleColor = [];
      const multipleColors = [];
      const clearanceColors = [];

      groupHits.forEach((hit) => {
        if (
          (hit.secondaryColorCode == null ||
            hit.primaryColorCode === hit.secondaryColorCode) &&
          !hit.isClearance
        ) {
          singleColor.push(hit);
        } else if (!hit.isClearance) {
          multipleColors.push(hit);
        } else {
          clearanceColors.push(hit);
        }
      });

      singleColor.sort(
        (a, b) =>
          colorCodeRanking.indexOf(a.primaryColorCode) -
          colorCodeRanking.indexOf(b.primaryColorCode),
      );

      multipleColors.sort(
        (a, b) =>
          colorCodeRanking.indexOf(a.primaryColorCode) -
            colorCodeRanking.indexOf(b.primaryColorCode) ||
          colorCodeRanking.indexOf(a.secondaryColorCode[0]) -
            colorCodeRanking.indexOf(b.secondaryColorCode[0]),
      );

      clearanceColors.sort(
        (a, b) =>
          colorCodeRanking.indexOf(a.primaryColorCode) -
          colorCodeRanking.indexOf(b.primaryColorCode),
      );

      const sortedGroupHits = [
        ...singleColor,
        ...multipleColors,
        ...clearanceColors,
      ];
      // console.log(sortedGroupHits);

      finalResult.push({
        itemNumber: group.itemNumber,
        colors: sortedGroupHits,
      });
    });

    setSelectedArticleData(finalResult);
    setSelectedIndex(
      finalResult.findIndex((result) => result.itemNumber === style.value),
    );

    // Process inventory data synchronously from Typesense data
    finalResult.forEach(({ itemNumber, colors }) => {
      colors.forEach((colorData) => {
        // Transform Typesense data to expected inventory format
        const inventoryData = processInventoryData(colorData, warehouses);
        
        // Update current inventory state
        setCurrentInventory((prevData) => ({
          ...prevData,
          ...inventoryData.data,
        }));
      });
    });

    // console.log("Done getting inventory");
    // console.log(currentInventory);

    setCurrentInventoryLoading(false);
  };

  // Memoize the refresh callback to prevent infinite re-renders
  const handleInventoryRefresh = useCallback(() => {
    // Only reload if we have a previous search to refresh
    if (lastSearchStyle && selectedArticleData.length > 0) {
      console.log(`Auto-reloading search results - fresh inventory data available`);
      onSelectStyle(lastSearchStyle);
    }
  }, [lastSearchStyle, selectedArticleData.length, onSelectStyle]);

  // Auto-reload when fresh inventory data is available (completion-based)
  const { isStale, minutesSinceUpdate } = useInventoryFreshness(handleInventoryRefresh); // Uses completion-based refresh by default

  return {
    selectedArticleData,
    currentInventory,
    currentInventoryLoading,
    selectedIndex,
    setSelectedIndex,
    onSelectStyle,
    isInventoryStale: isStale,
    minutesSinceInventoryUpdate: minutesSinceUpdate,
  };
};

export default useSelectedArticle;
